import { api } from "./api";

const CACHE_KEY = "chuzi:system:gradientBgMediaId:v2";

const BG_WIDTH = 1920;
const BG_HEIGHT = 1080;

/**
 * Render a dark atmospheric gradient matching DefaultSceneSlate
 * onto an OffscreenCanvas, return as PNG blob.
 */
function createGradientBlob(): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = BG_WIDTH;
  canvas.height = BG_HEIGHT;
  const ctx = canvas.getContext("2d")!;

  // Base fill
  ctx.fillStyle = "#0a0e1e";
  ctx.fillRect(0, 0, BG_WIDTH, BG_HEIGHT);

  // Atmospheric radial glow (top-left, blue)
  const glow = ctx.createRadialGradient(
    BG_WIDTH * 0.3, BG_HEIGHT * 0.2, 0,
    BG_WIDTH * 0.3, BG_HEIGHT * 0.2, BG_WIDTH * 0.7,
  );
  glow.addColorStop(0, "rgba(80,120,200,0.25)");
  glow.addColorStop(1, "rgba(10,14,30,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, BG_WIDTH, BG_HEIGHT);

  // Bottom fade
  const fade = ctx.createLinearGradient(0, BG_HEIGHT, 0, BG_HEIGHT * 0.5);
  fade.addColorStop(0, "rgba(0,0,0,0.65)");
  fade.addColorStop(0.5, "rgba(0,0,0,0.15)");
  fade.addColorStop(1, "transparent");
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, BG_WIDTH, BG_HEIGHT);

  // Scatter subtle star dots
  const stars = [
    [0.20, 0.30, 0.4], [0.70, 0.15, 0.3], [0.45, 0.65, 0.25],
    [0.80, 0.75, 0.35], [0.10, 0.80, 0.2], [0.55, 0.10, 0.3],
    [0.90, 0.45, 0.2], [0.35, 0.90, 0.15], [0.65, 0.40, 0.25],
    [0.15, 0.55, 0.2], [0.85, 0.20, 0.15], [0.50, 0.85, 0.2],
  ];
  for (const [x, y, alpha] of stars) {
    const grad = ctx.createRadialGradient(
      x * BG_WIDTH, y * BG_HEIGHT, 0,
      x * BG_WIDTH, y * BG_HEIGHT, 2,
    );
    grad.addColorStop(0, `rgba(255,255,255,${alpha})`);
    grad.addColorStop(1, "transparent");
    ctx.fillStyle = grad;
    ctx.fillRect(
      x * BG_WIDTH - 3, y * BG_HEIGHT - 3, 6, 6,
    );
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      "image/png",
    );
  });
}

/**
 * Returns the media_id for a system-level atmospheric gradient PNG in S3.
 * Uploads once and caches the id in localStorage for future calls.
 */
export async function getSystemBackgroundMediaId(): Promise<string> {
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) return cached;

  const blob = await createGradientBlob();
  const filename = "chuzi-system-gradient-bg.png";

  const { key, uploadUrl } = await api.media.uploadUrl({
    filename,
    contentType: "image/png",
  });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    xhr.setRequestHeader("Content-Type", "image/png");
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    });
    xhr.addEventListener("error", () => reject(new Error("Upload network error")));
    xhr.send(blob);
  });

  const media = await api.media.register({
    key,
    s3_key: key,
    filename,
    content_type: "image/png",
    title: "System Gradient Background",
    file_size: blob.size,
  });

  localStorage.setItem(CACHE_KEY, media.id);
  return media.id;
}
